import React, { useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';

import LogEntrySkeleton from '../../ConductorHubSkeleton/LogEntrySkeleton';

interface EarlierEntriesProps {
  /** Whether there is history before the first entry that the log has not read. */
  hasEarlier: boolean;
  /** Whether the log shows any entry, so that an empty one is not said to start anywhere. */
  hasEntries: boolean;
  /** Reads the page of history before the first entry into the log. Rejects if the read fails. */
  onLoadEarlier: () => Promise<void>;
  /** Whether a read this control did not start is bringing earlier entries in, as a stage change's does. */
  isReadingIn?: boolean;
}

const READING_IN_ENTRY_COUNT = 3;

const LINK_BUTTON_CLASS_NAME = 'p-0 font-medium normal-case text-teal-700 hover:bg-transparent';

/**
 * The top of the log: "Load earlier" while there is history before the first entry, and "Start of the log" once
 * there is none.
 *
 * A button, not the sentinel `InsightHistoryTimeline` pages on: the log opens at its top, so a sentinel would be in
 * view on every open and read a page each time. One page at a time, the button giving way to a skeleton while the
 * read is in flight; a read that fails says so and offers to try again, as the timeline does. It leaves the cursor
 * where it was, so trying again reads the same page. While a read started elsewhere is out, a few entry-shaped
 * placeholders stand where its entries will land.
 */
const EarlierEntries: React.FC<EarlierEntriesProps> = ({ hasEarlier, hasEntries, onLoadEarlier, isReadingIn }) => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  const handleLoadEarlier = () => {
    setStatus('loading');
    onLoadEarlier().then(
      () => setStatus('idle'),
      () => setStatus('error')
    );
  };

  if (isReadingIn) {
    return (
      <div role="status" aria-label="Reading earlier entries" className="mb-16 flex flex-col gap-16">
        {Array.from({ length: READING_IN_ENTRY_COUNT }, (_, index) => (
          <LogEntrySkeleton key={index} />
        ))}
      </div>
    );
  }

  if (status !== 'idle') {
    return (
      <div className="mb-16 flex items-center gap-8">
        {/* One skeleton whose error flag flips, so its loading span ends tagged as the error it was. */}
        <SkeletonTable rowsCount={1} columnsCount={1} isError={status === 'error'} wrapperPadding="0" />
        {status === 'error' ? (
          <Button variant="text" className={LINK_BUTTON_CLASS_NAME} onClick={handleLoadEarlier}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  if (hasEarlier) {
    return (
      <div className="mb-16">
        <Button variant="text" className={LINK_BUTTON_CLASS_NAME} onClick={handleLoadEarlier}>
          Load earlier
        </Button>
      </div>
    );
  }

  return hasEntries ? (
    <div className="mb-16">
      <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        Start of the log
      </Text>
    </div>
  ) : null;
};

export default EarlierEntries;
